import React from 'react'
import { View } from 'react-native'
import { Icon, Text } from '@src/components/base'
import { ImageStyle, LoadingFastImage } from '@src/components/LoadingFastImage'
import { NotificationInboxPresentation } from '@src/components/notifications/hooks/useNotificationInboxPresentation'
import { StyleService, useStyleSheet } from '@src/style/service'
import { UiIcon } from '@src/types'
import { getNotificationIconName } from './getNotificationIconName'

interface AlertContentRecord {
  content: string
  id: string
  icon?: UiIcon | null
  imageUrl?: string | null
}

interface AlertContentProps {
  iconTestID?: string
  notification: AlertContentRecord
  presentation: NotificationInboxPresentation
}

export const AlertContent = ({ iconTestID, notification, presentation }: AlertContentProps) => {
  const styles = useStyleSheet(themedStyles)
  const imageUrl = notification.imageUrl || undefined
  const hasImage = !!imageUrl
  const defaultAlertIcon = presentation.alertSeverity === 'warning' ? 'warning' : 'warning-circle'
  const iconName = getNotificationIconName(notification.icon) || defaultAlertIcon

  return (
    <View style={styles.container}>
      {hasImage ? (
        <View
          style={styles.alertImageContainer}
          testID={`AlertContent/ImageContainer/${notification.id}`}
        >
          <LoadingFastImage
            source={{ uri: imageUrl }}
            style={styles.alertImage as ImageStyle}
            resizeMode="contain"
            testID={`AlertContent/Image/${notification.id}`}
          />
        </View>
      ) : (
        <Icon
          name={iconName}
          size={24}
          weight="bold"
          style={styles.warningIcon}
          testID={iconTestID}
        />
      )}
      <View style={styles.content}>
        <Text type="regular" bold>
          {presentation.senderLabel}
        </Text>
        <Text type="regular">{notification.content}</Text>
      </View>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
  },
  warningIcon: {
    color: 'theme.warning.darkest',
  },
  alertImageContainer: {
    alignItems: 'center',
    backgroundColor: 'theme.transparent',
    borderColor: 'theme.surface.base3',
    borderRadius: 20,
    borderWidth: 1,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  alertImage: {
    height: 32,
    width: 32,
  },
  content: {
    flex: 1,
  },
})
